import { destination, polygon, type Units } from '@turf/turf';
import type { Feature, Point } from 'geojson';
import { Anchor, createAnchor } from './anchor';
import type { Coordinate } from './coordinate';
import {
  getGeoJsonPropertyCoordinate,
  getGeoJsonPropertyNumber,
  getGeoJsonPropertyText,
  setGeoJsonProperty,
} from './geojson';
import { setMode, type ShapeGeoJson } from 'shared/shapes/domain/shapeGeoJson';
import { ShapeMode } from 'shared/shapes/domain/shapeMode';

const meters: { units: Units } = { units: 'meters' };

interface CreatePreviewRectangle {
  anchor: Anchor;
  coordinate: Coordinate;
  heading: number;
  width: number;
  length: number;
  geofence: RectangleGeofence;
}

export interface RectangleGeofence {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface PreviewRectangle {
  shape: Feature;
  geofence: Feature;
}

export function createPreviewRectangle(options: CreatePreviewRectangle): ShapeGeoJson {
  const { anchor, coordinate, heading, width, length, geofence } = options;

  const startPoint = createAnchor({
    anchor,
    coordinate,
    heading,
    width,
    length,
  });

  const shape = createRectangle({
    point: startPoint,
    width,
    length,
    heading,
  });

  let geoFenceStartPosition = destination(startPoint, geofence.bottom, heading + 180, meters);

  geoFenceStartPosition = destination(geoFenceStartPosition, geofence.left, heading + 270, meters);

  const geofencePolygon = createRectangle({
    point: geoFenceStartPosition,
    width: width + geofence.left + geofence.right,
    length: length + geofence.top + geofence.bottom,
    heading,
  });

  setMode(shape, ShapeMode.Rectangle);

  return {
    shape,
    geofence: geofencePolygon,
  };
}

export interface Rectangle {
  point: Feature<Point>;
  width: number;
  length: number;
  heading: number;
}

export function createRectangle(rectangle: Rectangle) {
  const { point, heading, length, width } = rectangle;

  const corner0 = destination(point, 0, 0, meters);
  const corner1 = destination(corner0, length, heading, meters);
  const corner2 = destination(corner1, width, heading + 90, meters);
  const corner3 = destination(corner2, length, heading + 180, meters);

  const feature = polygon([
    [
      corner0.geometry.coordinates,
      corner1.geometry.coordinates,
      corner2.geometry.coordinates,
      corner3.geometry.coordinates,
      corner0.geometry.coordinates,
    ],
  ]);

  return feature;
}

export function geoJsonRectangle(feature: GeoJSON.Feature) {
  return {
    get heading() {
      return getHeading(feature);
    },

    set heading(value: number | undefined) {
      setHeading(feature, value);
    },

    get anchor() {
      return getAnchor(feature);
    },

    set anchor(value: Anchor | undefined) {
      setAnchor(feature, value);
    },

    get coordinate() {
      return getCoordinate(feature);
    },

    set coordinate(value: Coordinate | undefined) {
      setCoordinate(feature, value);
    },

    get width() {
      return getWidth(feature);
    },

    set width(value: number | undefined) {
      setWidth(feature, value);
    },

    get length() {
      return getLength(feature);
    },

    set length(value: number | undefined) {
      setLength(feature, value);
    },

    get geofenceBottom() {
      return getGeofenceBottom(feature);
    },

    set geofenceBottom(value: number | undefined) {
      setGeofenceBottom(feature, value);
    },

    get geofenceLeft() {
      return getGeofenceLeft(feature);
    },

    set geofenceLeft(value: number | undefined) {
      setGeofenceLeft(feature, value);
    },

    get geofenceRight() {
      return getGeofenceRight(feature);
    },

    set geofenceRight(value: number | undefined) {
      setGeofenceRight(feature, value);
    },

    get geofenceTop() {
      return getGeofenceTop(feature);
    },

    set geofenceTop(value: number | undefined) {
      setGeofenceTop(feature, value);
    },

    get label() {
      return getLabel(feature);
    },

    set label(value: string | undefined) {
      setLabel(feature, value);
    },
  };
}

const headingKey = 'direction';

export function getHeading(feature: GeoJSON.Feature) {
  return getGeoJsonPropertyNumber(feature, headingKey);
}

export function setHeading(feature: GeoJSON.Feature, value?: number) {
  setGeoJsonProperty(feature, headingKey, value);
}

const anchorKey = 'anchor';

export function getAnchor(feature: GeoJSON.Feature) {
  const text = getGeoJsonPropertyText(feature, anchorKey);
  if (text && Object.values(Anchor).includes(text as Anchor)) {
    return text as Anchor;
  }
}

export function setAnchor(feature: GeoJSON.Feature, value?: Anchor) {
  setGeoJsonProperty(feature, anchorKey, value);
}

const coordinateKey = 'startPosition';

export function getCoordinate(feature: GeoJSON.Feature) {
  return getGeoJsonPropertyCoordinate(feature, coordinateKey);
}

export function setCoordinate(feature: GeoJSON.Feature, coordinate?: Coordinate) {
  if (coordinate) {
    setGeoJsonProperty(feature, coordinateKey, coordinate);
  } else {
    setGeoJsonProperty(feature, coordinateKey, undefined);
  }
}

const widthKey = 'width';

export function getWidth(feature: GeoJSON.Feature) {
  return getGeoJsonPropertyNumber(feature, widthKey);
}

export function setWidth(feature: GeoJSON.Feature, value?: number) {
  setGeoJsonProperty(feature, widthKey, value);
}

const lengthKey = 'length';

export function getLength(feature: GeoJSON.Feature) {
  return getGeoJsonPropertyNumber(feature, lengthKey);
}

export function setLength(feature: GeoJSON.Feature, value?: number) {
  setGeoJsonProperty(feature, lengthKey, value);
}

const geofenceBottom = 'geoFenceBottom';

export function getGeofenceBottom(feature: GeoJSON.Feature) {
  return getGeoJsonPropertyNumber(feature, geofenceBottom);
}

export function setGeofenceBottom(feature: GeoJSON.Feature, value?: number) {
  setGeoJsonProperty(feature, geofenceBottom, value);
}

const geofenceTop = 'geoFenceTop';

export function getGeofenceTop(feature: GeoJSON.Feature) {
  return getGeoJsonPropertyNumber(feature, geofenceTop);
}

export function setGeofenceTop(feature: GeoJSON.Feature, value?: number) {
  setGeoJsonProperty(feature, geofenceTop, value);
}

const geofenceLeft = 'geoFenceLeft';

export function getGeofenceLeft(feature: GeoJSON.Feature) {
  return getGeoJsonPropertyNumber(feature, geofenceLeft);
}

export function setGeofenceLeft(feature: GeoJSON.Feature, value?: number) {
  setGeoJsonProperty(feature, geofenceLeft, value);
}

const geofenceRight = 'geoFenceRight';

export function getGeofenceRight(feature: GeoJSON.Feature) {
  return getGeoJsonPropertyNumber(feature, geofenceRight);
}

export function setGeofenceRight(feature: GeoJSON.Feature, value?: number) {
  setGeoJsonProperty(feature, geofenceRight, value);
}

const label = 'label';

export function getLabel(feature: GeoJSON.Feature) {
  return getGeoJsonPropertyText(feature, label);
}

export function setLabel(feature: GeoJSON.Feature, value?: string) {
  setGeoJsonProperty(feature, label, value);
}
